/**
 * La machine à états du 421 : des transitions pures, `(état, action) -> état`.
 *
 * Rien n'est mutable, rien ne vit hors de l'état (voir etat.ts). Le hasard est
 * injecté par un `Tireur`, jamais tiré ici : c'est ce qui permet de rejouer une
 * partie à l'identique et de comparer ce moteur au simulateur Python.
 *
 * Deux ordres de parcours coexistent, et les confondre casse la parité :
 *   - l'ordre du TOUR part de l'ouvreur et suit les sièges actifs ;
 *   - l'ordre d'un RAMPEAU suit les sièges actifs, sans tenir compte de l'ouvreur.
 */
import { evaluerMain } from "./combinaisons.js";
import { REGLES_MAISON, } from "./etat.js";
export class ActionRefusee extends Error {
}
// ------------------------------------------------------------------ création
export function creerPartie(noms, regles = REGLES_MAISON) {
    if (noms.length < 2 || noms.length > 6) {
        throw new ActionRefusee("il faut de 2 à 6 joueurs");
    }
    const joueurs = noms.map((nom, siege) => ({
        siege, nom, jetons: 0, estIA: false, niveauIA: 0,
    }));
    return {
        revision: 0,
        regles,
        phase: "CHARGE",
        masse: regles.masse,
        joueurs,
        actifs: joueurs.map((j) => j.siege),
        classement: [],
        manche: nouvelleManche(1, 0),
        perdant: null,
        evenements: [],
    };
}
function nouvelleManche(numero, ouvreur) {
    return {
        numero,
        ouvreur,
        lancersMax: null,
        tourDe: ouvreur,
        enCours: null,
        poses: {},
        rampeau: null,
        resolution: {},
    };
}
// ------------------------------------------------------------------- lecture
/** Sièges actifs à partir de l'ouvreur : l'ordre dans lequel on joue la manche. */
export function ordreTour(p) {
    const m = p.manche;
    if (!m)
        return [];
    const i = Math.max(0, p.actifs.indexOf(m.ouvreur));
    return [...p.actifs.slice(i), ...p.actifs.slice(0, i)];
}
/** Nombre de lancers autorisés au joueur courant. */
export function lancersAutorises(p) {
    const m = p.manche;
    if (!m)
        return 0;
    if (m.rampeau)
        return 1; // un rampeau, c'est un seul jet
    return m.lancersMax ?? 3;
}
/** Actions légales pour le joueur dont c'est le tour. */
export function actionsPossibles(p) {
    const m = p.manche;
    if (!m || m.tourDe === null || p.phase === "TERMINEE")
        return [];
    const enCours = m.enCours;
    const max = lancersAutorises(p);
    if (!enCours)
        return ["LANCER"];
    if (m.rampeau)
        return []; // le jet du rampeau est posé d'office
    const out = ["ARRETER"];
    if (enCours.lancers < max)
        out.push("LANCER", "GARDER");
    return out;
}
function jetons(p, siege) {
    return p.joueurs[siege]?.jetons ?? 0;
}
function avecJetons(p, siege, delta) {
    return p.joueurs.map((j) => (j.siege === siege ? { ...j, jetons: j.jetons + delta } : j));
}
// -------------------------------------------------------------- transitions
export function appliquer(p, action, tireur) {
    if (p.phase === "TERMINEE")
        throw new ActionRefusee("la partie est terminée");
    const m = p.manche;
    if (!m || m.tourDe === null)
        throw new ActionRefusee("aucun joueur ne doit agir");
    if (!actionsPossibles(p).includes(action.type)) {
        throw new ActionRefusee(`action ${action.type} impossible maintenant`);
    }
    switch (action.type) {
        case "LANCER": return lancer(p, tireur);
        case "GARDER": return garder(p, action.des);
        case "ARRETER": return arreter(p);
    }
}
function lancer(p, tireur) {
    const m = p.manche;
    const siege = m.tourDe;
    // Dans un rampeau, le jet est unique et posé immédiatement.
    if (m.rampeau) {
        const main = troisDes(tireur);
        const rampeau = {
            ...m.rampeau,
            jets: { ...m.rampeau.jets, [siege]: main },
        };
        const evs = [{ type: "LANCE", siege, main, lancers: 1 }];
        return suiteRampeau({ ...p, manche: { ...m, rampeau, tourDe: null } }, evs);
    }
    const precedent = m.enCours;
    let des;
    if (!precedent) {
        const main = troisDes(tireur);
        des = main.map((valeur, id) => ({ id: id, valeur, garde: false }));
    }
    else {
        const aRelancer = precedent.des.filter((d) => !d.garde).length;
        const neufs = tireur(aRelancer);
        let k = 0;
        des = precedent.des.map((d) => (d.garde ? d : { ...d, valeur: neufs[k++] }));
    }
    const lancers = (precedent?.lancers ?? 0) + 1;
    return suivant(p, {
        ...m,
        enCours: { des, lancers },
    }, [{ type: "LANCE", siege, main: mainDe(des), lancers }]);
}
function garder(p, indices) {
    const m = p.manche;
    const enCours = m.enCours;
    // Les index désignent des DÉS, pas des valeurs : « garder les deux 2 » serait
    // ambigu. On valide donc strictement.
    for (const i of indices) {
        if (!Number.isInteger(i) || i < 0 || i > 2) {
            throw new ActionRefusee(`index de dé invalide : ${i}`);
        }
    }
    const voulus = new Set(indices);
    const des = enCours.des.map((d) => ({ ...d, garde: voulus.has(d.id) }));
    return suivant(p, { ...m, enCours: { ...enCours, des } }, []);
}
function arreter(p) {
    const m = p.manche;
    const siege = m.tourDe;
    const enCours = m.enCours;
    const main = mainDe(enCours.des);
    const evs = [{ type: "POSE", siege, main }];
    // Le premier à poser fixe le nombre de lancers de toute la manche.
    let lancersMax = m.lancersMax;
    if (lancersMax === null) {
        lancersMax = enCours.lancers;
        if (lancersMax === 1)
            evs.push({ type: "SEC", siege });
    }
    const poses = { ...m.poses, [siege]: { main, lancers: enCours.lancers } };
    const restants = ordreTour(p).filter((s) => poses[s] === undefined);
    const manche = {
        ...m,
        lancersMax,
        poses,
        enCours: null,
        tourDe: restants[0] ?? null,
    };
    if (manche.tourDe !== null)
        return suivant(p, manche, evs);
    return resoudre({ ...p, manche, revision: p.revision + 1, evenements: evs });
}
function troisDes(tireur) {
    const [a, b, c] = tireur(3);
    return [a, b, c];
}
function mainDe(des) {
    return [des[0].valeur, des[1].valeur, des[2].valeur];
}
function suivant(p, manche, evs) {
    return { ...p, manche, revision: p.revision + 1, evenements: evs };
}
// --------------------------------------------------------------- résolution
/**
 * Désigne perdant, donneur et receveur, en déclenchant les rampeaux nécessaires.
 * Reprend là où elle s'était arrêtée grâce à `manche.resolution` : c'est ce qui
 * permet à une décharge de passer par deux rampeaux successifs sans rien perdre.
 */
function resoudre(p) {
    const m = p.manche;
    const scores = new Map();
    for (const siege of p.actifs) {
        scores.set(siege, evaluerMain(m.poses[siege].main).score);
    }
    const max = Math.max(...scores.values());
    const min = Math.min(...scores.values());
    // Jetons de la manche : valeur du MEILLEUR jet, doublée si la manche est sèche.
    const gagnante = p.actifs.find((s) => scores.get(s) === max);
    const doublee = p.regles.secDouble && m.lancersMax === 1;
    const pts = evaluerMain(m.poses[gagnante].main).jetons * (doublee ? 2 : 1);
    if (p.phase === "CHARGE") {
        let perdant = m.resolution.perdant;
        if (perdant === undefined) {
            const bas = p.actifs.filter((s) => scores.get(s) === min);
            if (bas.length > 1)
                return ouvrirRampeau(p, "PERDANT", "BAS", bas);
            perdant = bas[0];
        }
        return transferer(p, "MASSE", perdant, Math.min(pts, p.masse), doublee);
    }
    let donneur = m.resolution.donneur;
    if (donneur === undefined) {
        const haut = p.actifs.filter((s) => scores.get(s) === max);
        if (haut.length > 1)
            return ouvrirRampeau(p, "DONNEUR", "HAUT", haut);
        donneur = haut[0];
    }
    let receveur = m.resolution.receveur;
    if (receveur === undefined) {
        const reste = p.actifs.filter((s) => s !== donneur);
        const pire = Math.min(...reste.map((s) => scores.get(s)));
        const bas = reste.filter((s) => scores.get(s) === pire);
        if (bas.length > 1)
            return ouvrirRampeau(p, "RECEVEUR", "BAS", bas);
        receveur = bas[0];
    }
    return transferer(p, donneur, receveur, Math.min(pts, jetons(p, donneur)), doublee);
}
function ouvrirRampeau(p, role, sens, participants) {
    const m = p.manche;
    return {
        ...p,
        revision: p.revision + 1,
        manche: {
            ...m,
            rampeau: { role, sens, participants, jets: {} },
            tourDe: participants[0],
        },
        evenements: [...p.evenements, { type: "RAMPEAU", role, participants }],
    };
}
/** Après un jet de rampeau : au suivant, ou départage. */
function suiteRampeau(p, evs) {
    const m = p.manche;
    const r = m.rampeau;
    const suivantJoueur = r.participants.find((s) => r.jets[s] === undefined);
    if (suivantJoueur !== undefined) {
        return { ...p, revision: p.revision + 1,
            manche: { ...m, tourDe: suivantJoueur },
            evenements: evs };
    }
    const scores = r.participants.map((s) => evaluerMain(r.jets[s]).score);
    const cible = r.sens === "HAUT" ? Math.max(...scores) : Math.min(...scores);
    const gagnants = r.participants.filter((s) => evaluerMain(r.jets[s]).score === cible);
    if (gagnants.length > 1) {
        // Toujours à égalité : on relance entre les seuls ex æquo.
        return {
            ...p, revision: p.revision + 1,
            manche: { ...m, rampeau: { ...r, participants: gagnants, jets: {} },
                tourDe: gagnants[0] },
            evenements: [...evs, { type: "RAMPEAU", role: r.role, participants: gagnants }],
        };
    }
    const designe = gagnants[0];
    const resolution = { ...m.resolution };
    if (r.role === "PERDANT")
        resolution.perdant = designe;
    if (r.role === "DONNEUR")
        resolution.donneur = designe;
    if (r.role === "RECEVEUR")
        resolution.receveur = designe;
    return resoudre({
        ...p, revision: p.revision + 1,
        manche: { ...m, rampeau: null, resolution, tourDe: null },
        evenements: evs,
    });
}
function transferer(p, de, vers, montant, double) {
    let masse = p.masse;
    let joueurs = p.joueurs;
    if (de === "MASSE") {
        masse -= montant;
        joueurs = avecJetons({ ...p, joueurs }, vers, montant);
    }
    else {
        joueurs = avecJetons({ ...p, joueurs }, de, -montant);
        joueurs = avecJetons({ ...p, joueurs }, vers, montant);
    }
    const evs = [...p.evenements,
        { type: "TRANSFERT", de, vers, jetons: montant, double }];
    const apres = { ...p, masse, joueurs, evenements: evs };
    return p.phase === "CHARGE"
        ? apresCharge(apres, vers)
        : apresDecharge(apres, de, vers);
}
function apresCharge(p, perdant) {
    if (p.masse > 0)
        return manancheSuivante(p, perdant);
    // La masse est vide : ceux qui n'ont aucun jeton sont sauvés d'office.
    const sauves = p.actifs.filter((s) => jetons(p, s) === 0);
    const actifs = p.actifs.filter((s) => jetons(p, s) > 0);
    const evs = [...p.evenements,
        ...sauves.map((siege) => ({ type: "SAUVE", siege })),
        { type: "PHASE", phase: "DECHARGE" }];
    const decharge = {
        ...p, phase: "DECHARGE", actifs,
        classement: [...p.classement, ...sauves], evenements: evs,
    };
    if (actifs.length <= 1)
        return terminer(decharge, actifs[0]);
    return manancheSuivante(decharge, perdant);
}
function apresDecharge(p, donneur, receveur) {
    let etat = p;
    if (jetons(p, donneur) === 0) {
        etat = {
            ...p,
            actifs: p.actifs.filter((s) => s !== donneur),
            classement: [...p.classement, donneur],
            evenements: [...p.evenements, { type: "SAUVE", siege: donneur }],
        };
    }
    if (etat.actifs.length <= 1)
        return terminer(etat, etat.actifs[0]);
    return manancheSuivante(etat, receveur);
}
function manancheSuivante(p, ouvreur) {
    const suite = p.actifs.includes(ouvreur) ? ouvreur : p.actifs[0];
    return {
        ...p,
        revision: p.revision + 1,
        manche: nouvelleManche((p.manche?.numero ?? 0) + 1, suite),
    };
}
function terminer(p, perdant) {
    return {
        ...p,
        revision: p.revision + 1,
        phase: "TERMINEE",
        perdant,
        manche: null,
        evenements: [...p.evenements, { type: "FIN", perdant }],
    };
}
// ------------------------------------------------------------------- utilitaires
/** Classement final, du premier sauvé au perdant. */
export function classementFinal(p) {
    return p.perdant === null ? p.classement : [...p.classement, p.perdant];
}
/** Vrai quand plus personne n'a à jouer. */
export function terminee(p) {
    return p.phase === "TERMINEE";
}
//# sourceMappingURL=moteur.js.map